import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, Clock, MapPin, Users, CalendarDays, List, ChevronLeft, ChevronRight, Sparkles, PartyPopper, CalendarCheck } from 'lucide-react'
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, parseISO } from 'date-fns'
import { useCollection } from '../../hooks/useFirestore'
import { useRSVPForm } from '../../hooks/useForm'
import { events as defaultEvents } from '../../data/seedData'
import { formatDate, getCountdown, isFutureDate } from '../../utils/helpers'
import { Modal, Input, Textarea } from '../../components/common'

const categories = [
  { id: 'all', label: 'All Events', gradient: 'from-gray-500 to-gray-600' },
  { id: 'worship', label: 'Worship', gradient: 'from-primary-500 to-blue-500' },
  { id: 'special', label: 'Special', gradient: 'from-accent-500 to-orange-500' },
  { id: 'youth', label: 'Youth', gradient: 'from-secondary-500 to-purple-500' },
  { id: 'prayer', label: 'Prayer', gradient: 'from-emerald-500 to-teal-500' },
  { id: 'outreach', label: 'Outreach', gradient: 'from-blue-500 to-indigo-500' },
]

const EventCard = ({ event, onClick, index }) => {
  const [countdown, setCountdown] = useState(getCountdown(event.date))

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(getCountdown(event.date))
    }, 1000)
    return () => clearInterval(timer)
  }, [event.date])

  const category = categories.find(c => c.id === event.category) || categories[0]
  // Parse date as local to avoid timezone issues
  const dateParts = event.date.split('-')
  const eventDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
  const day = eventDate.getDate()
  const month = eventDate.toLocaleString('default', { month: 'short' }).toUpperCase()

  return (
    <motion.div
      className="bg-white rounded-3xl shadow-soft hover:shadow-soft-lg overflow-hidden cursor-pointer group"
      whileHover={{ y: -8 }}
      onClick={() => onClick(event)}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={event.imageUrl || 'https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=600'}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-dark/20 to-transparent" />

        {/* Countdown Badge */}
        {!countdown.expired && (
          <motion.div
            className="absolute top-4 right-4 bg-gradient-to-r from-accent-500 to-accent-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", bounce: 0.5 }}
          >
            {countdown.days > 0 ? `${countdown.days}d ${countdown.hours}h` : countdown.hours > 0 ? `${countdown.hours}h ${countdown.minutes}m` : 'Today!'}
          </motion.div>
        )}

        {/* Date Badge */}
        <div className="absolute bottom-4 left-4 bg-white rounded-2xl p-3 text-center min-w-[70px] shadow-lg">
          <span className="block text-xs text-primary-600 font-bold uppercase">{month}</span>
          <span className="block text-2xl font-heading font-bold text-dark-100">{day}</span>
        </div>
      </div>

      <div className="p-6">
        {/* Category Badge */}
        <span className={`inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r ${category.gradient} text-white text-xs font-bold mb-3 shadow-sm`}>
          {category.label}
        </span>

        <h3 className="font-heading font-bold text-xl text-dark-100 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
          {event.title}
        </h3>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-dark-300">
            <div className="p-1.5 rounded-lg bg-primary-50">
              <Clock className="w-3.5 h-3.5 text-primary-500" />
            </div>
            <span>{event.time}{event.endTime && ` - ${event.endTime}`}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-dark-300">
            <div className="p-1.5 rounded-lg bg-secondary-50">
              <MapPin className="w-3.5 h-3.5 text-secondary-500" />
            </div>
            <span className="truncate">{event.location}</span>
          </div>
          {event.capacity && (
            <div className="flex items-center gap-2 text-sm text-dark-300">
              <div className="p-1.5 rounded-lg bg-accent-50">
                <Users className="w-3.5 h-3.5 text-accent-500" />
              </div>
              <span>Capacity: {event.capacity}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

const EventModal = ({ event, isOpen, onClose }) => {
  const [showRSVP, setShowRSVP] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    attendees: 1,
    notes: '',
  })

  const { submit, submitting, submitted, error, reset } = useRSVPForm(event?.id)

  if (!event) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    await submit(formData, event)
  }

  const handleClose = () => {
    setShowRSVP(false)
    reset()
    onClose()
  }

  const addToCalendar = () => {
    const startDate = new Date(`${event.date}T${event.time.replace(/\s*(AM|PM)/i, '')}`)
    const title = encodeURIComponent(event.title)
    const location = encodeURIComponent(event.address || event.location)
    const details = encodeURIComponent(event.description)
    const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${format(startDate, "yyyyMMdd'T'HHmmss")}/${format(startDate, "yyyyMMdd'T'HHmmss")}&location=${location}&details=${details}`
    window.open(gcalUrl, '_blank')
  }

  const category = categories.find(c => c.id === event.category) || categories[0]

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" title={event.title}>
      <div className="space-y-6">
        {/* Event Image */}
        <div className="relative h-64 rounded-2xl overflow-hidden">
          <img
            src={event.imageUrl || 'https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=800'}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark/50 to-transparent" />
          <span className={`absolute top-4 left-4 inline-flex items-center px-3 py-1.5 rounded-full bg-gradient-to-r ${category.gradient} text-white text-sm font-bold shadow-lg`}>
            {category.label}
          </span>
        </div>

        {/* Event Details */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary-50">
                <Calendar className="w-5 h-5 text-primary-600" />
              </div>
              <span className="text-dark-300">{formatDate(event.date, 'EEEE, MMMM d, yyyy')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-secondary-50">
                <Clock className="w-5 h-5 text-secondary-600" />
              </div>
              <span className="text-dark-300">{event.time}{event.endTime && ` - ${event.endTime}`}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-accent-50">
                <MapPin className="w-5 h-5 text-accent-600" />
              </div>
              <span className="text-dark-300">{event.location}</span>
            </div>
            {event.capacity && (
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-50">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-dark-300">Capacity: {event.capacity} people</span>
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-5 border border-primary-100">
            <h4 className="font-heading font-bold text-dark-100 mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary-600" />
              Quick Actions
            </h4>
            <div className="space-y-2">
              <motion.button
                onClick={addToCalendar}
                className="w-full px-4 py-3 rounded-xl border-2 border-primary-500 text-primary-600 font-semibold hover:bg-primary-500 hover:text-white transition-colors flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <CalendarCheck className="w-4 h-4" />
                Add to Calendar
              </motion.button>
              {event.address && (
                <motion.button
                  onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(event.address)}`, '_blank')}
                  className="w-full px-4 py-3 rounded-xl border-2 border-secondary-500 text-secondary-600 font-semibold hover:bg-secondary-500 hover:text-white transition-colors flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <MapPin className="w-4 h-4" />
                  Get Directions
                </motion.button>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <h4 className="font-heading font-bold text-dark-100 mb-2">About This Event</h4>
          <p className="text-dark-300 leading-relaxed">{event.description}</p>
        </div>

        {/* RSVP Section */}
        {event.requiresRSVP && (
          <div className="border-t pt-6">
            {!showRSVP && !submitted ? (
              <div className="text-center">
                <p className="text-dark-300 mb-4">This event requires registration.</p>
                <motion.button
                  onClick={() => setShowRSVP(true)}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold shadow-lg shadow-primary-500/25"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <PartyPopper className="w-5 h-5" />
                  Register Now
                </motion.button>
              </div>
            ) : submitted ? (
              <motion.div
                className="text-center bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                >
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
                <h4 className="font-heading font-bold text-xl text-emerald-900 mb-2">Registration Successful!</h4>
                <p className="text-emerald-700">You're registered for this event. We'll send a confirmation to your email.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h4 className="font-heading font-bold text-dark-100">Event Registration</h4>

                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Full Name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                  <Input
                    label="Email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                  <Input
                    label="Number of Attendees"
                    type="number"
                    min="1"
                    max="20"
                    required
                    value={formData.attendees}
                    onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                  />
                </div>

                <Textarea
                  label="Notes (optional)"
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any special requests or dietary needs?"
                />

                {error && (
                  <p className="text-red-500 text-sm bg-red-50 px-4 py-2 rounded-xl">{error}</p>
                )}

                <div className="flex gap-3">
                  <motion.button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold shadow-lg disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : null}
                    Complete Registration
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => setShowRSVP(false)}
                    className="px-6 py-3 rounded-xl text-dark-300 hover:bg-light-100 font-semibold transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

const CalendarView = ({ events, currentMonth, onEventClick }) => {
  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd })

  const getEventsForDay = (day) => {
    return events.filter(event => {
      // Parse date as local to avoid timezone issues
      const dateParts = event.date.split('-')
      const eventDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
      return isSameDay(eventDate, day)
    })
  }

  return (
    <div className="bg-white rounded-3xl shadow-soft overflow-hidden border border-gray-100">
      {/* Calendar Header */}
      <div className="grid grid-cols-7 bg-gradient-to-r from-primary-50 to-secondary-50">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-4 text-center font-heading font-bold text-dark-100 text-sm">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7">
        {Array.from({ length: monthStart.getDay() }).map((_, i) => (
          <div key={`empty-${i}`} className="p-4 border-t border-r border-gray-100 min-h-[100px] bg-light-100/50" />
        ))}

        {days.map(day => {
          const dayEvents = getEventsForDay(day)
          const isToday = isSameDay(day, new Date())

          return (
            <div
              key={day.toISOString()}
              className={`p-2 border-t border-r border-gray-100 min-h-[100px] ${
                isToday ? 'bg-primary-50' : ''
              }`}
            >
              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                isToday ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white' : 'text-dark-300'
              }`}>
                {format(day, 'd')}
              </span>

              <div className="mt-1 space-y-1">
                {dayEvents.slice(0, 2).map(event => (
                  <button
                    key={event.id}
                    onClick={() => onEventClick(event)}
                    className="w-full text-left text-xs p-1.5 rounded-lg bg-gradient-to-r from-secondary-100 to-secondary-50 text-secondary-700 truncate hover:from-secondary-200 hover:to-secondary-100 font-medium transition-colors"
                  >
                    {event.title}
                  </button>
                ))}
                {dayEvents.length > 2 && (
                  <span className="text-xs text-dark-300 font-medium">+{dayEvents.length - 2} more</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const Events = () => {
  const [view, setView] = useState('list')
  const [activeCategory, setActiveCategory] = useState('all')
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedEvent, setSelectedEvent] = useState(null)

  const { data: events } = useCollection('events', {
    orderByField: 'date',
    orderDirection: 'asc',
  })

  const displayEvents = events?.length > 0
    ? events.filter(e => e.isPublished !== false)
    : defaultEvents

  const upcomingEvents = displayEvents.filter(e => isFutureDate(e.date))

  const filteredEvents = activeCategory === 'all'
    ? upcomingEvents
    : upcomingEvents.filter(e => e.category === activeCategory)

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-100 to-dark" />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/20"
              whileHover={{ scale: 1.05 }}
            >
              <Calendar className="w-4 h-4 text-secondary-400" />
              What's Happening
            </motion.span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6">
              Upcoming <span className="bg-gradient-to-r from-secondary-400 to-accent-400 bg-clip-text text-transparent">Events</span>
            </h1>
            <p className="text-xl text-white/70">
              Join us for worship, fellowship, and community. There's always something happening at DLBC Clarksville!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter & View Controls */}
      <section className="py-6 bg-white border-b sticky top-20 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between gap-4">
            {/* Categories */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map(category => (
                <motion.button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-5 py-2.5 rounded-full font-semibold whitespace-nowrap transition-all ${
                    activeCategory === category.id
                      ? `bg-gradient-to-r ${category.gradient} text-white shadow-lg`
                      : 'bg-light-100 text-dark-300 hover:bg-light-200'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category.label}
                </motion.button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-4">
              {view === 'calendar' && (
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    className="p-2 hover:bg-light-100 rounded-xl transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <ChevronLeft className="w-5 h-5 text-dark-300" />
                  </motion.button>
                  <span className="font-heading font-bold min-w-[140px] text-center text-dark-100">
                    {format(currentMonth, 'MMMM yyyy')}
                  </span>
                  <motion.button
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    className="p-2 hover:bg-light-100 rounded-xl transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <ChevronRight className="w-5 h-5 text-dark-300" />
                  </motion.button>
                </div>
              )}

              <div className="flex border-2 border-light-200 rounded-2xl overflow-hidden bg-light-100">
                <motion.button
                  onClick={() => setView('list')}
                  className={`p-3 ${view === 'list' ? 'bg-primary-500 text-white' : 'text-dark-300'} transition-colors`}
                  whileTap={{ scale: 0.95 }}
                >
                  <List className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={() => setView('calendar')}
                  className={`p-3 ${view === 'calendar' ? 'bg-primary-500 text-white' : 'text-dark-300'} transition-colors`}
                  whileTap={{ scale: 0.95 }}
                >
                  <CalendarDays className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Content */}
      <section className="py-24 bg-gradient-to-b from-white to-light-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            {view === 'list' ? (
              <motion.div
                key="list"
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {filteredEvents.map((event, index) => (
                  <EventCard key={event.id} event={event} onClick={setSelectedEvent} index={index} />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="calendar"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <CalendarView
                  events={displayEvents}
                  currentMonth={currentMonth}
                  onEventClick={setSelectedEvent}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {filteredEvents.length === 0 && view === 'list' && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-light-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-dark-300" />
              </div>
              <p className="text-dark-300 text-lg">No upcoming events in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Event Modal */}
      <EventModal
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </>
  )
}

export default Events
